import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { getFolderTree } from "../api";
import searchIconSrc from "../assets/search.svg";
import chevronIcon from "../assets/chevron-right.svg";
import folderIcon from "../assets/folder.svg";
import { compareFolderRows } from "./tagOrder";
import "../styles/gateway/tag-picker.css";

/**
 * FolderPicker — selects a folder ID from the live folder tree.
 *
 * Mirrors TagPicker structurally but selects FOLDERS (not tags). The
 * UI shows folder names, the value emitted to the parent is the
 * folder's ID. A "(root)" entry is always offered so callers can pick
 * "no folder" without leaving the field empty.
 *
 * @param {string} value - Current folder ID ("" means root)
 * @param {function} onChange - (folderId: string, folderName: string) => void
 * @param {string} [placeholder]
 */
export default function FolderPicker({ value, onChange, placeholder = "(root)" }) {
  const [open, setOpen] = useState(false);
  const [tree, setTree] = useState(null);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  // Load tree on mount AND on each open. The mount fetch is what makes
  // the input show a friendly folder name when the picker first renders
  // with a non-empty `value` — otherwise displayName falls back to the
  // raw ID until the user actually opens the dropdown. The on-open
  // refresh keeps the menu list fresh when folders are added in another
  // tab.
  useEffect(() => {
    getFolderTree({ lite: true, countsOnly: true })
      .then((data) => setTree(data?.browsers || []))
      .catch(() => setTree([]));
  }, []);
  useEffect(() => {
    if (!open) return;
    getFolderTree({ lite: true, countsOnly: true })
      .then((data) => setTree(data?.browsers || []))
      .catch(() => {});
  }, [open]);

  // Position the dropdown — same fixed-position trick TagPicker uses so
  // the menu doesn't get clipped by overflow:hidden ancestors.
  const positionMenu = useCallback(() => {
    const el = inputRef.current;
    const menu = menuRef.current;
    if (!el || !menu) return;
    const r = el.getBoundingClientRect();
    const menuH = menu.offsetHeight;
    const fitsBelow = window.innerHeight - r.bottom - 4 >= menuH;
    menu.style.position = "fixed";
    menu.style.top = fitsBelow ? `${Math.round(r.bottom + 4)}px` : `${Math.round(r.top - menuH - 4)}px`;
    menu.style.left = `${Math.round(r.left)}px`;
    menu.style.width = `${Math.round(r.width)}px`;
  }, []);

  const menuCallbackRef = useCallback((node) => {
    menuRef.current = node;
    if (node) positionMenu();
  }, [positionMenu]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    return () => {
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [open, positionMenu]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
      setSearch("");
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Build a parent → children index for the tree view, and a flat list
  // for filtered search. The folder-tree response has the same shape as
  // /api/folders/tree (an array of "browsers", which are folders).
  const { byParent, allFolders } = useMemo(() => {
    if (!tree) return { byParent: {}, allFolders: [] };
    const byParent = {};
    const allFolders = [];
    for (const b of tree) {
      if (!b || b.id === "__root__") continue;
      allFolders.push(b);
      const pid = b.parentId || "__root__";
      if (!byParent[pid]) byParent[pid] = [];
      byParent[pid].push(b);
    }
    Object.values(byParent).forEach((folders) => folders.sort(compareFolderRows));
    allFolders.sort(compareFolderRows);
    return { byParent, allFolders };
  }, [tree]);

  const handleSelect = (folderId, folderName) => {
    onChange(folderId, folderName);
    setOpen(false);
    setSearch("");
  };

  const q = search.trim().toLowerCase();
  const filteredFolders = q
    ? allFolders.filter((f) => (f.name || "").toLowerCase().includes(q))
    : null;

  // Display value: prefer the loaded folder's name when we know it, fall
  // back to the raw ID, then placeholder. We compute this once per render
  // so renames in the tree refresh the label automatically next time the
  // tree loads.
  const displayName = useMemo(() => {
    if (!value) return "";
    const found = allFolders.find((f) => f.id === value);
    return found ? found.name : value;
  }, [value, allFolders]);

  return (
    <div className="tpWrap" ref={wrapRef}>
      <div
        className="gwInput tpInput"
        ref={inputRef}
        onClick={() => setOpen(!open)}
        style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
      >
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {displayName || <span style={{ color: "var(--gw-text-disabled)" }}>{placeholder}</span>}
        </span>
        {value && (
          <span
            className="tpClear"
            onClick={(e) => {
              e.stopPropagation();
              onChange("", "");
            }}
            title="Clear selection"
          >
            &times;
          </span>
        )}
        <span className="gwSelect-caret" />
      </div>

      {open && (
        <div ref={menuCallbackRef} className="tpMenu">
          <div style={{ padding: "8px 8px 4px" }}>
            <div className="gwSearch" style={{ width: "100%", height: 32 }}>
              <img src={searchIconSrc} alt="" className="gwSearchIcon" />
              <input
                placeholder="Search folders..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                style={{ fontSize: 12 }}
              />
            </div>
          </div>

          <div className="tpList">
            {/* Always offer "(root)" so users can clear without leaving the field empty. */}
            <button
              type="button"
              className="tpFolder"
              onClick={() => handleSelect("", "")}
              style={{ paddingLeft: 8, fontStyle: "italic", color: "var(--gw-text-secondary)" }}
            >
              <img src={folderIcon} alt="" className="tpIcon gwIcon" />
              <span className="tpName">(root)</span>
            </button>

            {filteredFolders && (
              <>
                {filteredFolders.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className="tpFolder"
                    onClick={() => handleSelect(f.id, f.name)}
                    style={{ paddingLeft: 8 }}
                  >
                    <img src={folderIcon} alt="" className="tpIcon gwIcon" />
                    <span className="tpName">{f.name}</span>
                  </button>
                ))}
                {filteredFolders.length === 0 && (
                  <div className="tpEmpty">No folders match.</div>
                )}
              </>
            )}

            {!filteredFolders && tree && (
              <FolderTree
                byParent={byParent}
                parentId="__root__"
                onSelect={handleSelect}
                depth={0}
              />
            )}

            {!tree && <div className="tpEmpty">Loading folders...</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function FolderTree({ byParent, parentId, onSelect, depth }) {
  const folders = byParent[parentId] || [];
  return (
    <>
      {folders.map((folder) => (
        <FolderNode
          key={folder.id}
          folder={folder}
          byParent={byParent}
          onSelect={onSelect}
          depth={depth}
        />
      ))}
    </>
  );
}

function FolderNode({ folder, byParent, onSelect, depth }) {
  const [open, setOpen] = useState(false);
  const childFolders = byParent[folder.id] || [];
  const indent = 8 + depth * 16;

  // Click row → select. Chevron click → expand/collapse without selecting.
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
        {childFolders.length > 0 ? (
          <button
            type="button"
            className="tpFolder"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(!open);
            }}
            style={{ paddingLeft: indent, paddingRight: 4, flex: "0 0 auto", width: "auto", border: "none" }}
            title={open ? "Collapse" : "Expand"}
          >
            <img
              src={chevronIcon}
              alt=""
              className="tpChevron gwIcon"
              style={{ transform: open ? "rotate(90deg)" : "rotate(0)", transition: "transform 0.15s" }}
            />
          </button>
        ) : (
          <span style={{ width: indent + 16 }} />
        )}
        <button
          type="button"
          className="tpFolder"
          onClick={() => onSelect(folder.id, folder.name)}
          style={{ paddingLeft: 0, flex: 1 }}
        >
          <img src={folderIcon} alt="" className="tpIcon gwIcon" />
          <span className="tpName">{folder.name}</span>
          {childFolders.length > 0 && <span className="tpCount">({childFolders.length})</span>}
        </button>
      </div>
      {open && childFolders.length > 0 && (
        <div>
          {childFolders.map((cf) => (
            <FolderNode
              key={cf.id}
              folder={cf}
              byParent={byParent}
              onSelect={onSelect}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}
